import { usePush } from '../push/usePush.ts'
import { Button, Card, SectionTitle } from './ui.tsx'

/**
 * Activación de avisos.
 *
 * El botón es explícito y nunca se dispara solo al abrir la app: iOS da una
 * única oportunidad de pedir el permiso, y si se deniega hay que ir a Ajustes a
 * mano. Conviene que lo pulsen cuando ya han visto para qué sirve la app.
 */
export function NotificationsCard() {
  const push = usePush()

  if (!push.supported) {
    return (
      <>
        {/* "Notificaciones" y no "Avisos": en el dominio un aviso es un
            warning sobre una tarea, y mezclar los dos nombres confunde. */}
        <SectionTitle>Notificaciones</SectionTitle>
        <Card>
          <p className="text-sm text-[var(--color-muted)]">
            Este navegador no admite notificaciones. En iPhone hace falta iOS 16.4 o superior.
          </p>
        </Card>
      </>
    )
  }

  // El fallo más común y más silencioso: abrir en Safari en vez de desde el
  // icono. La suscripción falla sin mensaje de error, así que se avisa antes.
  if (push.needsHomeScreen) {
    return (
      <>
        {/* "Notificaciones" y no "Avisos": en el dominio un aviso es un
            warning sobre una tarea, y mezclar los dos nombres confunde. */}
        <SectionTitle>Notificaciones</SectionTitle>
        <Card>
          <p className="mb-2 font-semibold">⚠️ Añade la app a la pantalla de inicio</p>
          <p className="text-sm text-[var(--color-muted)]">
            En Safari los avisos no funcionan. Toca el botón <strong>Compartir</strong>, elige{' '}
            <strong>Añadir a pantalla de inicio</strong> y abre la app{' '}
            <strong>desde el icono nuevo</strong>. Luego vuelve aquí y actívalos.
          </p>
        </Card>
      </>
    )
  }

  return (
    <>
      <SectionTitle>Notificaciones</SectionTitle>
      <Card>
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[15px]">
              {push.subscribed ? 'Avisos activados' : 'Avisos desactivados'}
            </p>
            <p className="text-xs text-[var(--color-muted)]">
              {push.subscribed
                ? 'Recibirás avisos de tareas, descuentos y bonus.'
                : 'Sin ellos no te enterarás de un aviso hasta abrir la app.'}
            </p>
          </div>
          {push.subscribed ? (
            <Button variant="secondary" disabled={push.busy} onClick={() => void push.disable()}>
              Desactivar
            </Button>
          ) : (
            <Button disabled={push.busy} onClick={() => void push.enable()}>
              {push.busy ? 'Activando…' : 'Activar avisos'}
            </Button>
          )}
        </div>

        {push.subscribed && (
          <button
            onClick={() => void push.sendTest()}
            disabled={push.busy}
            className="mt-3 text-sm font-medium text-[var(--color-brand)]"
          >
            Enviar una notificación de prueba
          </button>
        )}

        {push.permission === 'denied' && (
          <p className="mt-3 rounded-lg bg-[var(--color-warn)]/15 px-3 py-2 text-xs text-[var(--color-warn)]">
            Los avisos están bloqueados para esta app. Actívalos en Ajustes → Notificaciones →
            Tareas.
          </p>
        )}

        {push.error && (
          <p className="mt-3 rounded-lg bg-[var(--color-bad)]/12 px-3 py-2 text-xs text-[var(--color-bad)]">
            {push.error}
          </p>
        )}
      </Card>
    </>
  )
}
